/**
 * BRICK Schema Icon Mappings
 *
 * Centralized icon definitions for all BRICK Schema equipment and entity types.
 * This file serves as the single source of truth for equipment icons across the application.
 *
 * Icon Format: Material Design Icons (https://fonts.google.com/icons)
 *
 * Usage:
 *   import { getBrickIcon } from '@/composables/brick-schema/config/brickIconMappings'
 *   const icon = getBrickIcon('brick:AHU')
 */

/**
 * Equipment Icon Mappings
 * Maps BRICK classes to Material Design icon names
 */
export const EQUIPMENT_ICONS = {
  // ==========================================
  // HVAC Equipment
  // ==========================================
  'brick:AHU': 'air',
  'brick:Air_Handler_Unit': 'air',
  'brick:Rooftop_Unit': 'sensor_door',
  'brick:RTU': 'sensor_door',
  'brick:Fan_Coil_Unit': 'ac_unit',
  'brick:FCU': 'ac_unit',
  'brick:Variable_Air_Volume_Box': 'stairs',
  'brick:VAV': 'stairs',
  'brick:Constant_Air_Volume_Box': 'inbox',
  'brick:CAV': 'inbox',
  'brick:Terminal_Unit': 'account_tree',

  // Chillers & Cooling
  'brick:Chiller': 'ac_unit',
  'brick:Water_Cooled_Chiller': 'ac_unit',
  'brick:Air_Cooled_Chiller': 'ac_unit',
  'brick:Cooling_Tower': 'water_damage',
  'brick:Condenser': 'water_drop',

  // Boilers & Heating
  'brick:Boiler': 'local_fire_department',
  'brick:Hot_Water_System': 'local_fire_department',
  'brick:Heat_Exchanger': 'compare_arrows',
  'brick:Heater': 'whatshot',
  'brick:Heat_Wheel': 'settings_brightness',

  // Pumps
  'brick:Pump': 'water_pump',
  'brick:Water_Pump': 'water_pump',
  'brick:Chilled_Water_Pump': 'water_pump',
  'brick:Hot_Water_Pump': 'water_pump',
  'brick:Condenser_Water_Pump': 'water_pump',

  // Fans & Air Movement
  'brick:Fan': 'settings_input_antenna',
  'brick:Supply_Fan': 'settings_input_antenna',
  'brick:Return_Fan': 'settings_input_antenna',
  'brick:Exhaust_Fan': 'settings_input_antenna',

  // Dampers & Valves
  'brick:Damper': 'tune',
  'brick:Valve': 'tune',
  'brick:Control_Valve': 'tune',

  // Filters
  'brick:Filter': 'filter_alt',
  'brick:Air_Filter': 'filter_alt',

  // ==========================================
  // Electrical Equipment
  // ==========================================

  // Switchboards & Panels
  'brick:Main_Switchboard': 'electrical_services',
  'brick:Mechanical_Services_Switchboard': 'power',
  'brick:Distribution_Board': 'developer_board',
  'brick:Switchboard': 'electrical_services',
  'brick:Switchgear': 'electrical_services',
  'brick:Breaker_Panel': 'developer_board',
  'brick:Panel': 'developer_board',

  // Control Panels
  'brick:DDC_Panel': 'settings_remote',
  'brick:Control_Panel': 'settings_remote',
  'brick:Motor_Control_Center': 'settings_applications',
  'brick:VFD_Panel': 'tune',

  // Transformers & Power Distribution
  'brick:Transformer': 'power',
  'brick:UPS': 'battery_charging_full',
  'brick:Generator': 'bolt',
  'brick:Emergency_Generator': 'bolt',

  // ==========================================
  // Metering Equipment (NEW)
  // ==========================================

  'brick:Electrical_Meter': 'speed',
  'brick:Power_Meter': 'speed',
  'brick:Energy_Meter': 'speed',
  'brick:Building_Electrical_Meter': 'analytics',
  'brick:Electric_Meter': 'speed',

  // Gas & Water Meters
  'brick:Gas_Meter': 'speed',
  'brick:Natural_Gas_Meter': 'speed',
  'brick:Water_Meter': 'water_drop',
  'brick:Chilled_Water_Meter': 'water_drop',
  'brick:Hot_Water_Meter': 'water_drop',

  // Thermal Meters
  'brick:Thermal_Power_Meter': 'thermostat',
  'brick:Heat_Meter': 'thermostat',

  // ==========================================
  // Building Control Systems
  // ==========================================

  // Controllers
  'brick:BMS_Controller': 'settings_remote',
  'brick:DDC_Controller': 'settings_remote',
  'brick:Controller': 'settings_remote',
  'brick:Zone_Controller': 'settings_remote',
  'brick:Terminal_Unit_Controller': 'settings_remote',

  // Lighting
  'brick:Lighting': 'lightbulb',
  'brick:Lighting_System': 'lightbulb',
  'brick:Lighting_Zone': 'lightbulb',

  // ==========================================
  // Sensors & Devices
  // ==========================================

  'brick:Temperature_Sensor': 'thermostat',
  'brick:Humidity_Sensor': 'water_drop',
  'brick:Pressure_Sensor': 'compress',
  'brick:Flow_Sensor': 'water',
  'brick:CO2_Sensor': 'co2',
  'brick:Occupancy_Sensor': 'person',
  'brick:Motion_Sensor': 'directions_walk',
  'brick:Differential_Pressure_Sensor': 'compare_arrows',

  // ==========================================
  // Building Spaces
  // ==========================================

  'brick:Room': 'meeting_room',
  'brick:Office': 'business',
  'brick:Conference_Room': 'groups',
  'brick:Laboratory': 'science',
  'brick:Mechanical_Room': 'settings',
  'brick:Electrical_Room': 'electrical_services',
  'brick:Server_Room': 'dns',
  'brick:IDF': 'router',
  'brick:Plant_Room': 'factory',
  'brick:HVAC_Zone': 'thermostat',

  // ==========================================
  // Circuits (NEW)
  // ==========================================

  'brick:Electrical_Circuit': 'cable',
  'brick:Circuit_Breaker_Point': 'power_settings_new',

  // ==========================================
  // Default Fallbacks
  // ==========================================

  'default:equipment': 'widgets',
  'default:device': 'memory',
  'default:point': 'sensors',
  'default:space': 'location_on',
  'default:system': 'hub',
};

/**
 * Equipment Icon Colors
 * Maps BRICK classes to Quasar color names for visual differentiation
 */
export const EQUIPMENT_COLORS = {
  // HVAC - Blue tones
  'brick:AHU': 'blue',
  'brick:VAV': 'light-blue',
  'brick:Chiller': 'cyan',

  // Heating - Orange/Red tones
  'brick:Boiler': 'deep-orange',
  'brick:Heater': 'orange',

  // Electrical - Yellow/Amber tones
  'brick:Main_Switchboard': 'amber',
  'brick:Mechanical_Services_Switchboard': 'amber-8',
  'brick:Distribution_Board': 'amber-6',
  'brick:DDC_Panel': 'yellow-9',

  // Meters - Green tones (monitoring/data)
  'brick:Electrical_Meter': 'green',
  'brick:Power_Meter': 'green',
  'brick:Energy_Meter': 'light-green',
  'brick:Water_Meter': 'blue-grey',

  // Controllers - Purple tones
  'brick:Controller': 'purple',
  'brick:BMS_Controller': 'deep-purple',

  // Default
  'default': 'grey',
};

/**
 * Get BRICK icon for a given BRICK class
 *
 * @param {string} brickClass - Full BRICK class (e.g., 'brick:AHU')
 * @param {string} fallback - Fallback icon if no match found
 * @returns {string} Material Design icon name
 */
export function getBrickIcon(brickClass, fallback = 'widgets') {
  if (!brickClass) {return fallback;}

  // Direct match
  if (EQUIPMENT_ICONS[brickClass]) {
    return EQUIPMENT_ICONS[brickClass];
  }

  // Partial match (case-insensitive)
  const lowerClass = brickClass.toLowerCase();

  // Try matching without the brick: prefix
  const withoutPrefix = brickClass.replace('brick:', '');
  if (EQUIPMENT_ICONS[`brick:${withoutPrefix}`]) {
    return EQUIPMENT_ICONS[`brick:${withoutPrefix}`];
  }

  // Pattern matching for common types
  if (lowerClass.includes('meter')) {return 'speed';}
  if (lowerClass.includes('pump')) {return 'water_pump';}
  if (lowerClass.includes('fan')) {return 'settings_input_antenna';}
  if (lowerClass.includes('sensor')) {return 'sensors';}
  if (lowerClass.includes('controller')) {return 'settings_remote';}
  if (lowerClass.includes('valve') || lowerClass.includes('damper')) {return 'tune';}
  if (lowerClass.includes('switchboard') || lowerClass.includes('panel')) {return 'electrical_services';}
  if (lowerClass.includes('chiller')) {return 'ac_unit';}
  if (lowerClass.includes('boiler') || lowerClass.includes('heater')) {return 'local_fire_department';}
  if (lowerClass.includes('ahu') || lowerClass.includes('air_handler')) {return 'air';}

  // Return fallback
  return fallback;
}

/**
 * Get BRICK color for a given BRICK class
 *
 * @param {string} brickClass - Full BRICK class (e.g., 'brick:AHU')
 * @param {string} fallback - Fallback color if no match found
 * @returns {string} Quasar color name
 */
export function getBrickColor(brickClass, fallback = 'grey') {
  if (!brickClass) {return fallback;}

  // Direct match
  if (EQUIPMENT_COLORS[brickClass]) {
    return EQUIPMENT_COLORS[brickClass];
  }

  // Pattern matching
  const lowerClass = brickClass.toLowerCase();
  if (lowerClass.includes('meter')) {return 'green';}
  if (lowerClass.includes('switchboard') || lowerClass.includes('panel')) {return 'amber';}
  if (lowerClass.includes('controller')) {return 'purple';}
  if (lowerClass.includes('chiller') || lowerClass.includes('cool')) {return 'cyan';}
  if (lowerClass.includes('boiler') || lowerClass.includes('heat')) {return 'deep-orange';}

  return fallback;
}

/**
 * Check if a BRICK class represents a meter
 *
 * @param {string} brickClass - Full BRICK class
 * @returns {boolean} True if it's a meter class
 */
export function isMeterClass(brickClass) {
  if (!brickClass) {return false;}
  const lowerClass = brickClass.toLowerCase();
  return lowerClass.includes('meter');
}

/**
 * Check if a BRICK class represents electrical equipment
 *
 * @param {string} brickClass - Full BRICK class
 * @returns {boolean} True if it's electrical equipment
 */
export function isElectricalEquipment(brickClass) {
  if (!brickClass) {return false;}
  const lowerClass = brickClass.toLowerCase();
  return (
    lowerClass.includes('switchboard') ||
    lowerClass.includes('panel') ||
    lowerClass.includes('transformer') ||
    lowerClass.includes('ups') ||
    lowerClass.includes('generator') ||
    lowerClass.includes('breaker') ||
    lowerClass.includes('electrical')
  );
}

/**
 * Check if a BRICK class represents HVAC equipment
 *
 * @param {string} brickClass - Full BRICK class
 * @returns {boolean} True if it's HVAC equipment
 */
export function isHVACEquipment(brickClass) {
  if (!brickClass) {return false;}
  const lowerClass = brickClass.toLowerCase();
  return (
    lowerClass.includes('ahu') ||
    lowerClass.includes('vav') ||
    lowerClass.includes('fcu') ||
    lowerClass.includes('chiller') ||
    lowerClass.includes('boiler') ||
    lowerClass.includes('pump') ||
    lowerClass.includes('fan') ||
    lowerClass.includes('hvac')
  );
}

export default {
  EQUIPMENT_ICONS,
  EQUIPMENT_COLORS,
  getBrickIcon,
  getBrickColor,
  isMeterClass,
  isElectricalEquipment,
  isHVACEquipment,
};
